import type { ComponentType } from 'react';

import type { DtoOrderOrderTypeEnum } from '@repo/api-types/order-retrieval';
import type { AssetType } from '@repo/types/asset';

import { AssetCancelModal } from '../modals/asset-cancel-modal';
import { AssetDeleteModal } from '../modals/asset-delete-modal';
import { AssetExportModal } from '../modals/asset-export-modal';
import { AssetMessageModal } from '../modals/asset-message-modal';
import { type ActionModalProps, MODAL, type ModalType } from '../modals/asset-modal.types';
import { AssetPaymentModal } from '../modals/asset-payment-modal';
import { AssetPickingListPdfModal } from '../modals/asset-picking-list-pdf-modal';
import { AssetQuestionnaireModal } from '../modals/asset-questionnaire-modal/asset-questionnaire-modal.component';
import { AssetShareModal } from '../modals/asset-share-modal';
import { AssetSummaryModal } from '../modals/asset-summary-modal';
import { AssetCancelAction } from './asset-context-buttons/actions/asset-cancel-action';
import { AssetDeleteAction } from './asset-context-buttons/actions/asset-delete-action';
import { AssetExportAction } from './asset-context-buttons/actions/asset-export-action';
import { KitchenLinkAction } from './asset-context-buttons/actions/asset-link-action';
import { AssetMessageAction } from './asset-context-buttons/actions/asset-message-action';
import { AssetOrderHistoryAction } from './asset-context-buttons/actions/asset-order-history-action';
import { AssetOrderSummaryAction } from './asset-context-buttons/actions/asset-order-summary-action';
import { AssetPaymentAction } from './asset-context-buttons/actions/asset-payment-action';
import { AssetPickingListPdfAction } from './asset-context-buttons/actions/asset-picking-list-pdf-action';
import { AssetQuestionnaireAction } from './asset-context-buttons/actions/asset-questionnaire-action/asset-questionnaire-action.component';
import { AssetShareAction } from './asset-context-buttons/actions/asset-share-action';
import type { AssetActionProps } from './asset-context-buttons/asset-context-buttons.types';

type ActionKey = ModalType;

type Actions = Partial<Record<ActionKey, ComponentType<AssetActionProps>>>;

type ActionModals = Partial<Record<ActionKey, ComponentType<ActionModalProps>>>;

export const ACTION_BUTTONS_CONFIG: Actions = {
    SHARE: AssetShareAction,
    MESSAGE: AssetMessageAction,
    EXPORT: AssetExportAction,
    ORDER_SUMMARY: AssetOrderSummaryAction,
    QUESTIONNAIRE: AssetQuestionnaireAction,
    ORDER_HISTORY: AssetOrderHistoryAction,
    ORDER_PAYMENT: AssetPaymentAction,
    PICKING_LIST_PDF: AssetPickingListPdfAction,
    CANCEL: AssetCancelAction,
    DELETE: AssetDeleteAction,
    KITCHEN_LINK: KitchenLinkAction, // temporary link to kitchen checker POC
} as const;

export const ASSET_ACTION_MODALS_CONFIG: ActionModals = {
    SHARE: AssetShareModal,
    CANCEL: AssetCancelModal,
    ORDER_SUMMARY: AssetSummaryModal,
    QUESTIONNAIRE: AssetQuestionnaireModal,
    DELETE: AssetDeleteModal,
    EXPORT: AssetExportModal,
    MESSAGE: AssetMessageModal,
    ORDER_PAYMENT: AssetPaymentModal,
    PICKING_LIST_PDF: AssetPickingListPdfModal,
} as const;

type OrderTypeActionConfig = Partial<Record<DtoOrderOrderTypeEnum, Array<keyof typeof ACTION_BUTTONS_CONFIG>>>;

export type AssetActionsConfig = {
    [key in AssetType]?: key extends 'order' ? OrderTypeActionConfig : Array<keyof typeof ACTION_BUTTONS_CONFIG>;
};

export const ASSET_ACTIONS_CONFIG: AssetActionsConfig = {
    order: {
        SALESORDER: [MODAL.SHARE, MODAL.MESSAGE, MODAL.EXPORT, MODAL.ORDER_SUMMARY, MODAL.ORDER_HISTORY, MODAL.CANCEL],
        ISOM: [
            MODAL.SHARE,
            MODAL.MESSAGE,
            MODAL.EXPORT,
            MODAL.ORDER_SUMMARY,
            MODAL.PICKING_LIST_PDF,
            MODAL.QUESTIONNAIRE,
            MODAL.ORDER_HISTORY,
            MODAL.ORDER_PAYMENT,
            MODAL.CANCEL,
        ],
        SHLIST: [MODAL.SHARE, MODAL.EXPORT, MODAL.ORDER_SUMMARY],
    },
    draft: [
        MODAL.SHARE,
        MODAL.MESSAGE,
        MODAL.EXPORT,
        MODAL.ORDER_SUMMARY,
        MODAL.PICKING_LIST_PDF,
        MODAL.DELETE,
        MODAL.KITCHEN_LINK,
    ],
    quote: [MODAL.SHARE, MODAL.MESSAGE, MODAL.EXPORT, MODAL.ORDER_SUMMARY, MODAL.DELETE],
} as const;
